/**
 * PWE Weight Matrix — versioned channel/dimension weight editor.
 *
 * Loads the active 9×12 matrix + version history, renders the editable grid,
 * and drives Save-as-new-version / Activate / Rollback against
 * channelWeights.js (proxied through quillAdminService). Activate & Rollback
 * surface the backend's documented `effective_within_seconds` (60s cache TTL)
 * in a confirmation dialog, since every one of these three actions writes to
 * the PRODUCTION Quill database and activation changes what real members are
 * served.
 *
 * Ported from quill-app/quill-admin's WeightMatrixPage.tsx (Phase 4.1).
 * Redux (weightsSlice) replaced with local useState/useEffect calling
 * quillApi.weights.* directly. Confirmation uses the shared
 * components/quill/common/QuillActionConfirm (see that file for why it isn't
 * the design-system components/help/ActionConfirm) instead of the original's
 * ad hoc inline MUI Dialog.
 */

import React from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Stack,
  TextField,
} from '@mui/material';
import SaveIcon from '@mui/icons-material/Save';
import UndoIcon from '@mui/icons-material/Undo';
import RefreshIcon from '@mui/icons-material/Refresh';
import { GridOn as WeightsIcon } from '@mui/icons-material';
import {
  quillApi,
  WeightMatrix,
  WeightHistoryRow,
  QuillApiError,
} from '../../../services/quillAdminService';
import { useQuillHealth } from '../QuillHealthContext';
import { HelpTip } from '../../help/HelpTip';
import QuillActionConfirm from '../common/QuillActionConfirm';
import WeightMatrixGrid from './WeightMatrixGrid';
import VersionHistoryPanel from './VersionHistoryPanel';
import { buildFullMatrix, hasEdits } from './matrixUtils';

type Confirm =
  | { kind: 'save' }
  | { kind: 'activate'; version: number }
  | { kind: 'rollback' }
  | null;

const WeightMatrixPage: React.FC = () => {
  const { status } = useQuillHealth();

  const [matrix, setMatrix] = React.useState<WeightMatrix | null>(null);
  const [history, setHistory] = React.useState<WeightHistoryRow[]>([]);
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const [edits, setEdits] = React.useState<Record<string, string>>({});
  const [confirm, setConfirm] = React.useState<Confirm>(null);
  const [toast, setToast] = React.useState<string | null>(null);
  const [busy, setBusy] = React.useState(false);
  // F10: the rationale for a change to 135 numbers. It used to be collected by
  // the API and thrown away (there was no column); migration 572 gives it one.
  const [note, setNote] = React.useState('');

  const reload = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [m, h] = await Promise.all([quillApi.weights.getActiveMatrix(), quillApi.weights.getHistory()]);
      setMatrix(m);
      setHistory(h);
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Failed to load matrix.');
    } finally {
      setLoading(false);
    }
  }, []);

  const reloadHistory = React.useCallback(async () => {
    try {
      setHistory(await quillApi.weights.getHistory());
    } catch {
      // Non-fatal — the existing history list stays on screen.
    }
  }, []);

  React.useEffect(() => {
    reload();
  }, [reload]);

  const dirty = matrix ? hasEdits(matrix, edits) : false;

  const handleCellChange = (key: string, value: string) => {
    setEdits((prev) => ({ ...prev, [key]: value }));
  };

  const doSave = async () => {
    if (!matrix) return;
    setBusy(true);
    try {
      const cells = buildFullMatrix(matrix, edits);
      const res = await quillApi.weights.createVersion(cells, note);
      setEdits({});
      setNote('');
      // Never claim the note was kept when the backend says it was dropped
      // (migration 572 not applied on this database).
      const noteWasDropped = note.trim().length > 0 && res.note_persisted === false;
      setToast(
        noteWasDropped
          ? `Saved as version ${res.weight_version} (inactive). Activate it to go live. `
            + 'Your note could NOT be saved — this database does not have the edit_note column yet '
            + '(migration 572), so it was not kept.'
          : `Saved as version ${res.weight_version} (inactive). Activate it to go live.`
      );
      await reloadHistory();
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Failed to save version.');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const doActivate = async (version: number) => {
    setBusy(true);
    try {
      const res = await quillApi.weights.activateVersion(version);
      setToast(
        `Version ${res.weight_version} activated — live within ${res.effective_within_seconds}s.`
      );
      await reload();
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Failed to activate version.');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const doRollback = async () => {
    setBusy(true);
    try {
      const res = await quillApi.weights.rollback();
      setToast(
        `Rolled back to version ${res.weight_version} (from ${res.rolled_back_from}) — live within ${res.effective_within_seconds}s.`
      );
      await reload();
    } catch (e) {
      setError(e instanceof QuillApiError ? e.message : 'Rollback failed.');
    } finally {
      setBusy(false);
      setConfirm(null);
    }
  };

  const handleConfirm = () => {
    if (confirm?.kind === 'save') doSave();
    else if (confirm?.kind === 'activate') doActivate(confirm.version);
    else if (confirm?.kind === 'rollback') doRollback();
  };

  const confirmCopy: { title: string; message: string; confirmLabel: string; emphasize?: boolean } | null =
    confirm?.kind === 'save'
      ? {
          title: 'Save as new version?',
          message:
            'This writes a new weight_version to the production Quill database as inactive. It will not affect live members until you activate it from the version history panel.',
          confirmLabel: 'Save version',
        }
      : confirm?.kind === 'activate'
      ? {
          title: 'Activate this version?',
          message: `Activating version ${confirm.version} changes the PWE weights served to real members in production. The change takes effect within 60 seconds (weightsRepo cache TTL).`,
          confirmLabel: 'Activate',
        }
      : confirm?.kind === 'rollback'
      ? {
          title: 'Roll back to previous version?',
          message:
            'Rolling back re-activates the previous weight version in the production Quill database, changing weights served to real members within 60 seconds (weightsRepo cache TTL).',
          confirmLabel: 'Roll back',
          emphasize: true,
        }
      : null;

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
        <WeightsIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Weight Matrix
        </Typography>
        <HelpTip id="quill-weights" />
      </Box>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        PWE channel/dimension weight versions, activation, and rollback.
      </Typography>

      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        {matrix?.weight_version != null && (
          <Chip label={`Active v${matrix.weight_version}`} color="success" />
        )}
        {matrix?.set_by && <Chip label={`set by ${matrix.set_by}`} variant="outlined" />}
        <Box sx={{ flexGrow: 1 }} />
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={reload} disabled={busy || loading}>
            Refresh
          </Button>
          <Button
            startIcon={<UndoIcon />}
            color="warning"
            variant="outlined"
            onClick={() => setConfirm({ kind: 'rollback' })}
            disabled={busy || loading}
          >
            Rollback
          </Button>
          <Button
            startIcon={<SaveIcon />}
            variant="contained"
            onClick={() => setConfirm({ kind: 'save' })}
            disabled={busy || loading || !dirty}
          >
            Save as new version
          </Button>
        </Stack>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Rows are evidence channels, columns are the Mirror-12 dimensions. Weights are 0–1
        (3 decimals). Editing a cell highlights it; "Save as new version" writes a complete
        108-cell version as <strong>inactive</strong> — activate it from the history panel to
        serve it to members.
      </Typography>

      {/* F10: the rationale, saved with the version (migration 572) and shown
          against it in the history panel below. Optional, because the versions
          already in the database have none and pretending otherwise would be
          the dishonesty this field exists to fix. */}
      <TextField
        label="Why this change (optional)"
        placeholder="e.g. raised prosody after the build-24 listening pass"
        // The field must not promise a save this database cannot make: until
        // migration 572 is applied, `note_column_present` is false and the note
        // is dropped. Say that BEFORE the operator types, not after.
        helperText={
          matrix && matrix.note_column_present === false
            ? 'This database cannot store notes yet (migration 572 has not been applied), so anything typed here will be dropped.'
            : 'Saved with the version and shown in the history below.'
        }
        error={matrix?.note_column_present === false && note.trim().length > 0}
        value={note}
        onChange={(e) => setNote(e.target.value)}
        fullWidth
        multiline
        minRows={2}
        size="small"
        disabled={busy || loading}
        inputProps={{ maxLength: 2000, 'aria-label': 'Why this change' }}
        sx={{ mb: 2 }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading && !matrix ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : matrix ? (
        <Stack spacing={3}>
          <WeightMatrixGrid
            matrix={matrix}
            edits={edits}
            onCellChange={handleCellChange}
            disabled={busy}
          />
          <VersionHistoryPanel
            history={history}
            onActivate={(version) => setConfirm({ kind: 'activate', version })}
          />
        </Stack>
      ) : (
        !loading && <Alert severity="info">No active weight matrix found.</Alert>
      )}

      {confirmCopy && (
        <QuillActionConfirm
          open={!!confirm}
          title={confirmCopy.title}
          message={confirmCopy.message}
          confirmLabel={confirmCopy.confirmLabel}
          destructive={confirmCopy.emphasize}
          loading={busy}
          pendingLabel="Applying…"
          onConfirm={handleConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}

      <Snackbar
        open={!!toast}
        autoHideDuration={6000}
        onClose={() => setToast(null)}
        message={toast || ''}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />

      <Typography
        variant="caption"
        sx={{ display: 'block', mt: 3, color: status === 'up' ? 'success.main' : 'text.disabled' }}
      >
        ● Local backend • {status === 'up' ? 'connected' : status}
      </Typography>
    </Box>
  );
};

export default WeightMatrixPage;
